import { useEffect, useState, type MouseEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useBackTo } from "@/hooks/useBackTo";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { ApiError } from "@/lib/apiClient";
import { toastApiError } from "@/lib/toast";
import { FIELD_MAX, tooLong } from "@/lib/fieldLimits";
import { Loading } from "@/components/Loading";
import { Notice } from "@/components/Notice";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { TextField, RichTextField, useAutoFocus, useUnsavedChangesWarning } from "@/components/form";
import { ticketTemplates as templatesApi } from "@/features/tickets/api/ticketTemplates.api";

const TEMPLATES_PATH = "/tickets/templates";

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "A template name is required.")
    .max(FIELD_MAX.ticket.templateName, tooLong("Name", FIELD_MAX.ticket.templateName)),
  content: z
    .string()
    .trim()
    .min(1, "Template content is required.")
    .max(FIELD_MAX.ticket.templateContent, tooLong("Content", FIELD_MAX.ticket.templateContent)),
});

type FormValues = z.infer<typeof schema>;

export default function TicketTemplateEditPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const backTo = useBackTo(TEMPLATES_PATH);
  const confirm = useConfirm();
  const isEdit = id !== undefined;

  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", content: "" },
  });

  useAutoFocus(form, "name", !isEdit);

  const { isDirty, isSubmitting } = form.formState;
  useUnsavedChangesWarning(isDirty);

  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    templatesApi.get(id)
      .then((t) => {
        if (cancelled) return;
        form.reset({ name: t.name, content: t.content });
        if (!t.canEdit) setLoadError("You don't have permission to edit this template.");
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(
          e instanceof ApiError && e.status === 404
            ? "Template not found."
            : e instanceof ApiError && e.status === 403
              ? e.message || "You don't have permission to view ticket templates."
              : e instanceof Error
                ? e.message
                : "Failed to load the template.",
        );
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, isEdit, form]);

  async function leaveBack(e: MouseEvent<HTMLAnchorElement>) {
    if (!isDirty || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    const discard = await confirm({
      title: "Discard unsaved changes?",
      description: "You have unsaved changes on this template. Leaving discards them.",
      confirmLabel: "Discard",
      cancelLabel: "Stay",
      destructive: true,
    });
    if (discard) navigate(backTo);
  }

  async function onSubmit(values: FormValues) {
    const payload = { name: values.name.trim(), content: values.content.trim() };
    setSaveError(null);
    try {
      if (isEdit && id) await templatesApi.update(id, payload);
      else await templatesApi.create(payload);
      form.reset(values);
      toast.success(isEdit ? "Template updated" : "Template created");
      navigate(backTo);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setSaveError(e.message || "You don't have permission to save templates.");
        return;
      }
      toastApiError(e, "Failed to save the template.");
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <header className="mb-6">
        <Link to={backTo} onClick={leaveBack} className="text-sm text-muted-foreground hover:underline">
          ← Back to templates
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-slate-800">
          {isEdit ? "Edit template" : "New template"}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Give the template a name your team will recognise, then write the content to insert.
        </p>
      </header>

      {loadError && <Notice tone="error">{loadError}</Notice>}

      {loading ? (
        <Loading>Loading template…</Loading>
      ) : loadError ? null : (
        <Card>
          <CardContent className="pt-6">
            {saveError && <Notice tone="error">{saveError}</Notice>}
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <TextField
                  control={form.control}
                  name="name"
                  label="Template name"
                  required
                  placeholder="Terminal not connecting"
                />
                <RichTextField
                  control={form.control}
                  name="content"
                  label="Template content"
                  required
                  placeholder="Write the description your team should start from…"
                />
                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button asChild variant="ghost">
                    <Link to={backTo}>Cancel</Link>
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Saving…" : "Save template"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
